import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import '~/assets/scss/pages/authentication.scss'
import EmailIcon from '~/assets/img/icons/email.svg'
import LockIcon from '~/assets/img/icons/lock.svg'
import { signInRequest } from '~/store/modules/auth/actions'
import { AuthLayout } from '../components/AuthLayout'
import { queries } from '~/constants/mediaQuery'

function Login() {
  const dispatch = useDispatch()
  const loading = useSelector(state => state?.auth?.loading)
  const mfaRequired = useSelector(state => state?.auth?.mfaRequired)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordShow, setPasswordShow] = useState(false)
  const [codeDigit1, setCodeDigit1] = useState('')
  const [codeDigit2, setCodeDigit2] = useState('')
  const [codeDigit3, setCodeDigit3] = useState('')
  const [codeDigit4, setCodeDigit4] = useState('')
  const [codeDigit5, setCodeDigit5] = useState('')
  const [codeDigit6, setCodeDigit6] = useState('')
  const [mfaRequiredState, setMfaRequiredState] = useState(false)
  const [formSent, setFormSent] = useState(false)
  const intl = useIntl()

  const CODE_DIGIT_FUNCTION = [
    setCodeDigit1,
    setCodeDigit2,
    setCodeDigit3,
    setCodeDigit4,
    setCodeDigit5,
    setCodeDigit6,
  ]

  function handleSubmit(e) {
    e.preventDefault()
    const code = `${codeDigit1}${codeDigit2}${codeDigit3}${codeDigit4}${codeDigit5}${codeDigit6}`
    dispatch(signInRequest(email, password, code))
    setFormSent(true)
  }

  function handleCodeKeyDown(event, setValue) {
    const elem = event.target
    const value = event.key
    const isBackspace = event.key === 'Backspace'
    const isArrowLeft = event.key === 'ArrowLeft'
    const isArrowRight = event.key === 'ArrowRight'

    if (isBackspace) {
      setValue('')
      elem.value = ''
      const previousElem = elem.previousElementSibling
      if (previousElem) previousElem.focus()
      return
    }

    if (isArrowLeft) {
      const previousElem = elem.previousElementSibling
      if (previousElem) previousElem.focus()
      return
    }

    if (isArrowRight) {
      const nextElem = elem.nextElementSibling
      if (nextElem) nextElem.focus()
      return
    }

    const formattedValue = value.replace(/[^0-9]/g, '')
    if (formattedValue) setValue(formattedValue)

    const nextElem = elem.nextElementSibling
    if (nextElem && formattedValue) nextElem.focus()
  }

  function handlePaste(event) {
    const elem = event.target
    const paste = (event.clipboardData || window.clipboardData).getData('text')
    const formattedPaste = paste.replace(/[^0-9]/g, '')
    const digits = formattedPaste.split('')

    let nextElem = elem
    for (let i = 0; i < digits.length; i++) {
      if (i === 0) {
        CODE_DIGIT_FUNCTION[i](digits[i])
        continue
      }
      nextElem = nextElem.nextElementSibling
      if (!nextElem) break
      if (i === digits.length - 1) nextElem.focus()
      CODE_DIGIT_FUNCTION[i](digits[i])
    }
  }

  useEffect(() => {
    if (formSent && mfaRequired) {
      setMfaRequiredState(true)
    }
  }, [formSent, mfaRequired, setMfaRequiredState])

  return (
    <AuthLayout
      title={
        mfaRequiredState ? 'Two factor authentication' : 'Access your account'
      }
      description={
        mfaRequiredState
          ? 'Confirm the authentication code that was sent to your email, {0}'
          : 'Manage your locations in a fast and scalable way.'
      }
      descriptionValues={[email.replace(/(.{4}).*@(.*)/g, '$1****@$2')]}
    >
      <LoginDiv>
        {mfaRequiredState ? (
          <div className="code-input">
            <div className="label-row">
              <label htmlFor="one-time-code">
                <FormattedMessage id="Enter your code:" />
              </label>
            </div>
            <div className="input-row">
              <input
                id="codeDigit1"
                type="value"
                minLength="1"
                maxLength="1"
                pattern="[0-9]"
                value={codeDigit1}
                onKeyDown={e => handleCodeKeyDown(e, setCodeDigit1)}
                onPaste={e => handlePaste(e)}
                placeholder="•"
                disabled={loading}
                data-op-ignore
                data-1p-ignore
                required
              ></input>
              <input
                id="codeDigit2"
                type="value"
                minLength="1"
                maxLength="1"
                pattern="[0-9]"
                value={codeDigit2}
                onKeyDown={e => handleCodeKeyDown(e, setCodeDigit2)}
                onPaste={e => handlePaste(e)}
                placeholder="•"
                disabled={loading}
                data-op-ignore
                data-1p-ignore
                required
              ></input>
              <input
                id="codeDigit3"
                type="value"
                minLength="1"
                maxLength="1"
                pattern="[0-9]"
                value={codeDigit3}
                onKeyDown={e => handleCodeKeyDown(e, setCodeDigit3)}
                onPaste={e => handlePaste(e)}
                placeholder="•"
                disabled={loading}
                data-op-ignore
                data-1p-ignore
                required
              ></input>
              <input
                id="codeDigit4"
                type="value"
                minLength="1"
                maxLength="1"
                pattern="[0-9]"
                value={codeDigit4}
                onKeyDown={e => handleCodeKeyDown(e, setCodeDigit4)}
                onPaste={e => handlePaste(e)}
                placeholder="•"
                disabled={loading}
                data-op-ignore
                data-1p-ignore
                required
              ></input>
              <input
                id="codeDigit5"
                type="value"
                minLength="1"
                maxLength="1"
                pattern="[0-9]"
                value={codeDigit5}
                onKeyDown={e => handleCodeKeyDown(e, setCodeDigit5)}
                onPaste={e => handlePaste(e)}
                placeholder="•"
                disabled={loading}
                data-op-ignore
                data-1p-ignore
                required
              ></input>
              <input
                id="codeDigit6"
                type="value"
                minLength="1"
                maxLength="1"
                pattern="[0-9]"
                value={codeDigit6}
                onKeyDown={e => handleCodeKeyDown(e, setCodeDigit6)}
                onPaste={e => handlePaste(e)}
                placeholder="•"
                disabled={loading}
                data-op-ignore
                data-1p-ignore
                required
              ></input>
            </div>
            <button
              className="proceed-button"
              data-loading={loading}
              disabled={
                !codeDigit1 ||
                !codeDigit2 ||
                !codeDigit3 ||
                !codeDigit4 ||
                !codeDigit5 ||
                !codeDigit6 ||
                loading
              }
              onClick={handleSubmit}
            >
              <FormattedMessage id="Continue" />
            </button>
          </div>
        ) : (
          <>
            <div className="text-input">
              <div className="label-row">
                <label htmlFor="email">
                  <FormattedMessage id="Email" />
                </label>
              </div>
              <div className="input-row">
                <img src={EmailIcon} alt="" />
                <input
                  id="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={loading}
                  placeholder={intl.formatMessage({ id: 'Enter your e-mail' })}
                />
              </div>
            </div>
            <div className="password-input">
              <div className="label-row">
                <label htmlFor="current-password">
                  <FormattedMessage id="Password" />
                </label>
                <a className="forgot-anchor" href="/forgot-password">
                  <FormattedMessage id="Forgot your password?" />
                </a>
              </div>
              <div className="input-row">
                <img src={LockIcon} alt="" />
                <input
                  id="current-password"
                  autoComplete="current-password"
                  type={passwordShow ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="••••••••••"
                />
                <button
                  onClick={() => setPasswordShow(prevState => !prevState)}
                >
                  <FormattedMessage id={passwordShow ? 'Hide' : 'Show'} />
                </button>
              </div>
            </div>
            <button
              className="proceed-button"
              data-loading={loading}
              disabled={!email.trim() || !password.trim() || loading}
              onClick={handleSubmit}
            >
              <FormattedMessage id="Log in" />
            </button>

            <div className="register-div">
              <div>
                <FormattedMessage id="Don't have an account?" />
              </div>
              <a>
                <FormattedMessage id="Start for free" />
              </a>
            </div>
          </>
        )}
      </LoginDiv>
    </AuthLayout>
  )
}

const LoginDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;

  .proceed-button {
    display: flex;
    max-width: 30.5625rem;
    justify-content: center;
    align-items: center;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 0.25rem;
    background-color: #7367f0;
    color: #fff;
    font-size: 1.125rem;
    font-weight: 700;

    &:disabled {
      background-color: #b9b3f7;
      cursor: not-allowed;
    }

    &[data-loading='true'] {
      background-color: #b9b3f7;
      cursor: wait;
    }

    @media (${queries.less.mds}) {
      max-width: 100%;
    }
  }

  .password-input {
    position: relative;
    display: flex;
    min-width: 30.5625rem;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;

    @media (${queries.less.mds}) {
      min-width: 100%;
    }

    .label-row {
      width: 100%;
      max-width: 30.5625rem;
      display: flex;
      justify-content: space-between;
      align-items: center;

      label {
        font-weight: 700;
        color: #000;
        font-size: 1rem;
      }

      @media (${queries.less.mds}) {
        max-width: 100%;
      }
    }

    .input-row {
      width: 100%;
      max-width: 30.5625rem;
      height: 3rem;

      input {
        height: 100%;
        width: 75%;
        border: 1px solid #d8d8d8;
        border-radius: 0.25rem 0 0 0.25rem;
        padding: 0.5rem 1rem 0.5rem 3rem;

        &::placeholder {
          color: #cfcfcf;
        }
      }

      button {
        height: 100%;
        width: 25%;
        border: 1px solid #d8d8d8;
        border-left: none;
        border-radius: 0 0.25rem 0.25rem 0;
        background-color: #fafafa;
        color: #6d7278;
        font-weight: 700;
        font-size: 1rem;
        padding: 0.5rem 1rem;
      }

      img {
        position: absolute;
        left: 1rem;
        bottom: -2px;
        transform: translateY(-50%);
      }

      @media (${queries.less.mds}) {
        max-width: 100%;
      }
    }
  }

  .text-input {
    position: relative;
    display: flex;
    min-width: 30.5625rem;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;

    @media (${queries.less.mds}) {
      min-width: 100%;
    }

    .label-row {
      label {
        font-weight: 700;
        color: #000;
        font-size: 1rem;
      }
    }

    .input-row {
      width: 100%;
      max-width: 30.5625rem;
      height: 3rem;

      input {
        height: 3rem;
        width: 100%;
        max-width: 30.5625rem;
        border: 1px solid #d8d8d8;
        border-radius: 0.25rem 0.25rem 0.25rem 0.25rem;
        padding: 0.5rem 1rem 0.5rem 3rem;

        &::placeholder {
          color: #cfcfcf;
        }

        @media (${queries.less.mds}) {
          max-width: 100%;
        }
      }

      img {
        position: absolute;
        left: 1rem;
        bottom: 4px;
        transform: translateY(-50%);
      }

      @media (${queries.less.mds}) {
        max-width: 100%;
      }
    }
  }

  .code-input {
    min-width: 30.5625rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 2rem;

    @media (${queries.less.mds}) {
      min-width: 24rem;
    }

    .label-row {
      width: 100%;
      max-width: 30.5625rem;
      display: flex;
      justify-content: space-between;
      align-items: center;

      label {
        color: #2d3748;
        font-size: 1rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.5rem;
      }

      @media (${queries.less.mds}) {
        max-width: 100%;
      }
    }

    .input-row {
      width: min-content;
      display: flex;
      align-items: center;
      justify-content: start;
      gap: 0.625rem;

      input {
        width: 3rem;
        height: 3rem;
        aspect-ratio: 1/1;
        border-radius: 0.25rem;
        border: 1px solid #e2e8f0;
        background-color: #fff;

        text-align: center;
        font-size: 1.125rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        &:focus {
          outline: none;
          border-color: #7367f0;
        }
      }
    }
  }

  .proceed-button {
    width: 100%;
    max-width: 30.5625rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 0.25rem;
    background-color: #7367f0;
    color: #fff;
    font-size: 1.125rem;
    font-weight: 700;

    &:disabled {
      background-color: #b9b3f7;
      cursor: not-allowed;
    }

    &[data-loading='true'] {
      background-color: #b9b3f7;
      cursor: wait;
    }

    @media (${queries.less.mds}) {
      max-width: 100%;
    }
  }

  .register-div {
    width: 100%;
    display: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: auto;

    div {
      color: #2d3748;
      font-size: 1rem;
      font-style: normal;
      font-weight: 400;
      line-height: 1.5rem;
    }

    a {
      height: 2.5rem;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.625rem 1rem;
      background-color: #38a169;
      border-radius: 0.25rem;

      font-size: 1rem;
      font-style: normal;
      font-weight: 600;
      line-height: 1.5rem;
      color: #fff;

      &:focus {
        color: #fff;
      }
    }

    @media (${queries.less.mds}) {
      display: flex;
    }
  }
`

export default Login
